// MiSuscripcion.js — Pestaña «Mi suscripción» del perfil (fase 5).
// Muestra el plan, el estado (vigente, por vencer, vencida o indefinida), la
// fecha de vencimiento con los días que quedan, los cursos incluidos y el botón
// para renovar. Datos de api/mi-suscripcion.php. `tema`: 'oscuro' (Learning) o 'claro'.
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import FormCancelar from './FormCancelar';

const ESTADOS = {
  vigente:    { txt: 'Activa', color: '#22c55e' },
  por_vencer: { txt: 'Por vencer', color: '#f59e0b' },
  vencida:    { txt: 'Vencida', color: '#ef4444' },
  indefinido: { txt: 'Acceso indefinido', color: '#818cf8' },
};
const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: 'numeric', month: 'long', year: 'numeric' }) : '');

const Caja = styled.div`
  --texto: ${p => (p.$oscuro ? '#c9d4e2' : '#475569')};
  --fuerte: ${p => (p.$oscuro ? '#fff' : '#0f172a')};
  --suave: ${p => (p.$oscuro ? 'rgba(255,255,255,.06)' : '#f1f5f9')};
  --borde: ${p => (p.$oscuro ? 'rgba(255,255,255,.1)' : '#e2e8f0')};
  --estado: ${p => p.$color};
  color: var(--texto);
  .tarjeta { position: relative; overflow: hidden; padding: 1.4rem 1.5rem; border-radius: 18px; box-shadow: inset 0 0 0 1px var(--borde);
    background: radial-gradient(120% 120% at 100% 0%, color-mix(in srgb, var(--estado) 18%, transparent), transparent 55%), var(--suave); }
  .arriba { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .plan small { display: block; font-size: .8rem; }
  .plan strong { display: block; margin-top: .15rem; font-size: 1.6rem; font-weight: 800; color: var(--fuerte); line-height: 1.1; }
  .estado { display: inline-flex; align-items: center; gap: .45rem; padding: .35rem .8rem; border-radius: 999px; font-size: .82rem; font-weight: 700;
    color: var(--estado); background: color-mix(in srgb, var(--estado) 14%, transparent); }
  .estado::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 3px color-mix(in srgb, currentColor 25%, transparent); }
  .cuenta { display: flex; align-items: baseline; gap: .5rem; margin-top: 1.3rem; }
  .cuenta b { font-size: 2.6rem; font-weight: 800; color: var(--fuerte); line-height: 1; font-variant-numeric: tabular-nums; }
  .barra { height: 8px; margin-top: .8rem; border-radius: 999px; background: color-mix(in srgb, var(--fuerte) 10%, transparent); overflow: hidden; }
  .barra i { display: block; height: 100%; border-radius: inherit; background: var(--estado); }
  .fecha { margin-top: .6rem; font-size: .88rem; }
  .fecha b { color: var(--fuerte); }
  h3 { margin: 1.6rem 0 .7rem; font-size: 1rem; font-weight: 700; color: var(--fuerte); }
  .cursos { display: flex; flex-wrap: wrap; gap: .45rem; }
  .cursos span { padding: .4rem .8rem; border-radius: 999px; font-size: .85rem; background: var(--suave); box-shadow: inset 0 0 0 1px var(--borde); }
  .botones { display: flex; gap: .6rem; flex-wrap: wrap; margin-top: 1.6rem; }
  .ppal, .sec { display: inline-flex; align-items: center; height: 46px; padding: 0 1.3rem; border-radius: 10px; font-weight: 700; text-decoration: none; }
  .ppal { background: ${p => p.$acento}; color: #fff; box-shadow: 0 10px 24px -12px ${p => p.$acento}; }
  .sec { background: var(--suave); color: var(--fuerte); box-shadow: inset 0 0 0 1px var(--borde); }
  .ppal:focus-visible, .sec:focus-visible { outline: 2px solid var(--fuerte); outline-offset: 2px; }
  .nota { margin-top: 1rem; font-size: .82rem; }
  ${p => p.$cargando && css`opacity: .6;`}
`;

const MiSuscripcion = ({ datos: datosIni, tema = 'oscuro', acento = '#E50914' }) => {
  const [cancelando, setCancelando] = useState(false);
  const [datos, setDatos] = useState(datosIni);
  useEffect(() => { setDatos(datosIni); }, [datosIni]);
  if (!datos) return <p>Cargando…</p>;
  const est = ESTADOS[datos.estado] || ESTADOS.indefinido;
  const dias = datos.dias_restantes;
  const meses = datos.plan?.meses;
  // Parte del periodo que ya pasó: con un plan de N meses, desde (vence − N meses).
  const pct = datos.vence_en && meses ? Math.max(0, Math.min(100, 100 - (Math.max(0, dias) / (meses * 30.4)) * 100)) : null;
  const renovar = datos.url_renovar || '/contactanos?tema=pagos';
  const externo = /^https?:/.test(renovar);
  const BotonRenovar = ({ children }) => (externo
    ? <a className="ppal" href={renovar} target="_blank" rel="noopener noreferrer">{children}</a>
    : <Link className="ppal" to={renovar}>{children}</Link>);

  return (
    <Caja $oscuro={tema === 'oscuro'} $color={est.color} $acento={acento}>
      <div className="tarjeta">
        <div className="arriba">
          <div className="plan"><small>Tu plan</small><strong>{datos.plan?.nombre || 'Sin plan asignado'}</strong></div>
          <span className="estado">{est.txt}</span>
        </div>
        {datos.vence_en ? (
          <>
            <div className="cuenta">
              {datos.estado === 'vencida'
                ? <><b>{Math.abs(dias)}</b><span>{Math.abs(dias) === 1 ? 'día desde que venció' : 'días desde que venció'}</span></>
                : <><b>{Math.max(0, dias)}</b><span>{dias === 1 ? 'día restante' : 'días restantes'}</span></>}
            </div>
            {pct !== null && <div className="barra" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label="Tiempo usado del plan"><i style={{ width: `${pct}%` }} /></div>}
            <div className="fecha">{datos.estado === 'vencida' ? 'Venció el' : 'Vence el'} <b>{fecha(datos.vence_en)}</b></div>
          </>
        ) : (
          <div className="fecha" style={{ marginTop: '1rem' }}>Tu acceso no tiene fecha de vencimiento.</div>
        )}
      </div>

      <h3>Cursos incluidos</h3>
      <div className="cursos">
        {datos.cursos?.length ? datos.cursos.map(c => <span key={c.id}>{c.titulo}</span>) : <span>Todos los cursos de la plataforma</span>}
      </div>

      {datos.cancelada && datos.estado !== 'vencida' && (
        <p className="nota" style={{ marginTop: '1.2rem' }}>Cancelaste la renovación: no se te volverá a cobrar y tienes acceso hasta el {fecha(datos.vence_en)}.</p>
      )}
      {cancelando ? (
        <div style={{ marginTop: '1.6rem' }}>
          <FormCancelar venceEn={datos.vence_en} tema={tema} acento={acento} onVolver={() => setCancelando(false)}
            onListo={() => { setCancelando(false); setDatos(d => ({ ...d, cancelada: true })); }} />
        </div>
      ) : datos.vence_en && (
        <div className="botones">
          <BotonRenovar>{datos.estado === 'vencida' ? 'Renovar suscripción' : 'Renovar o ampliar'}</BotonRenovar>
          <Link className="sec" to="/contactanos?tema=pagos">Tengo una pregunta</Link>
          {!datos.cancelada && datos.estado !== 'vencida' && (
            <button type="button" className="sec" onClick={() => setCancelando(true)} style={{ border: 0, font: 'inherit', cursor: 'pointer' }}>Cancelar renovación</button>
          )}
        </div>
      )}
      {datos.estado === 'vencida' && <p className="nota">Tu avance y tus certificados siguen guardados: al renovar, retomas donde quedaste.</p>}
    </Caja>
  );
};

export default MiSuscripcion;
